/**
 * Thin wrapper over the User Timing API for measuring the dynamic-component pipeline.
 *
 * Every design, layout, list and control on a page is compiled in the browser at render
 * time, so the cost of that pipeline is what decides whether a slow page is bound by the
 * network or by the main thread. Request counts are visible from outside; this is not.
 *
 * Entries land in `performance.getEntriesByType('measure')` and are read from there by the
 * measurement harness. The library also runs under Node, where the API may be absent, so
 * every call degrades to running the work unmeasured.
 */
const CAN_MEASURE = typeof performance !== 'undefined'
                 && typeof performance.mark === 'function'
                 && typeof performance.measure === 'function'

export const BUILD_MEASURE = 'ae-component-build'
export const COMPILE_MEASURE = 'ae-template-compile'
export const BUNDLE_FETCH_MEASURE = 'ae-ui-bundle-fetch'
export const BUNDLE_IMPORT_MEASURE = 'ae-ui-bundle-import'

// Separate names, because the two corpora are sized very differently and a combined figure
// would hide which one is costing the load.
export const DESIGN_BUNDLE_FETCH_MEASURE = 'ae-ui-design-bundle-fetch'
export const DESIGN_BUNDLE_IMPORT_MEASURE = 'ae-ui-design-bundle-import'
export const CONTROL_BUNDLE_FETCH_MEASURE = 'ae-ui-control-bundle-fetch'
export const CONTROL_BUNDLE_IMPORT_MEASURE = 'ae-ui-control-bundle-import'

function startMeasure(name: string) {
  const startMark = `${name}-start`
  performance.mark(startMark)

  return () => {
    try {
      performance.measure(name, startMark)
      performance.clearMarks(startMark)
    } catch {
      // Never let instrumentation break rendering.
    }
  }
}

export function measure<TResult>(name: string, run: () => TResult): TResult {
  if (!CAN_MEASURE) {
    return run()
  }

  // A failed build is still work the main thread did, so the measure is taken either way.
  const finish = startMeasure(name)

  try {
    return run()
  } finally {
    finish()
  }
}

/**
 * Wall-clock, not main-thread time - it spans awaits, so a measure that covers a fetch
 * includes time the main thread was free. Use it for whole phases, and `measure` for
 * synchronous work whose cost is the main thread itself.
 */
export async function measureAsync<TResult>(name: string, run: () => Promise<TResult>): Promise<TResult> {
  if (!CAN_MEASURE) {
    return run()
  }

  const finish = startMeasure(name)

  try {
    return await run()
  } finally {
    finish()
  }
}
